/* ============================================
   APP COMPONENT
   The Creative Orchestra - GEMINI Framework
   Main application wrapper with providers
   ============================================ */

import { useState, useEffect, Suspense } from 'react';
import { RouterProvider } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AudioProvider } from '@context/AudioContext';
import { ThemeProvider } from '@context/ThemeContext';
import { LoadingScreen, PageLoader } from '@components/common';
import router from './router';

/**
 * App Component
 * Root component that wraps the entire application with:
 * - Theme Provider (for work-specific theming)
 * - Audio Provider (for ambient music)
 * - Loading screen
 * - Error boundaries
 */
function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingComplete, setLoadingComplete] = useState(false);

  // Handle loading complete
  const handleLoadingComplete = () => {
    setLoadingComplete(true);
    // Small delay before hiding loader
    setTimeout(() => setIsLoading(false), 100);
  };

  // Preload critical resources
  useEffect(() => {
    const preloadResources = async () => {
      try {
        // Preload fonts (if using custom fonts)
        if (document.fonts) {
          await document.fonts.ready;
        }

        // Small minimum delay for smooth UX
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.warn('Resource preload failed:', error);
      }
    };

    preloadResources();
  }, []);

  return (
    <ThemeProvider>
      <AudioProvider>
        {/* Initial loading screen */}
        <AnimatePresence>
          {isLoading && (
            <LoadingScreen 
              isLoading={!loadingComplete}
              minDuration={1500}
              onComplete={handleLoadingComplete}
            />
          )}
        </AnimatePresence>

        {/* Main application */}
        <Suspense fallback={<PageLoader message="Loading page..." />}>
          <RouterProvider 
            router={router}
            fallbackElement={<PageLoader message="Loading..." />}
          />
        </Suspense>
      </AudioProvider>
    </ThemeProvider>
  );
}

export default App;
